import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, CreditCard, Calendar, Tag, CreditCard as CreditCardIcon, ArrowRight } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MasterLabel from '../common/MasterLabel';

// Presentational — the completed-payments list arrives via props from the patient
// dashboard socket. Both tabs share the same data and split paid/unpaid
// client-side, so switching tabs needs no refetch.
const PaymentCompletionList = ({ payments: paymentsData, loading: patientsPaymentLoading }) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('paid');


  // Split the completed payments into paid / not-paid client-side, limit to 6.
  const isPaidStatus = (s) => ['Paid', 'paid', 'collected', 'Completed'].includes(s);
  const getPayments = () => {
    if (!paymentsData?.appointments) return [];
    const filteredData = paymentsData.appointments.filter((payment) =>
      activeTab === 'paid'
        ? isPaidStatus(payment.payment?.status)
        : !isPaidStatus(payment.payment?.status)
    );
    return filteredData.slice(0, 6);
  };

  const payments = getPayments();

  const renderPaymentItem = (payment, index) => {
    // Format date from API
    const paymentDate = new Date(payment.payment.date);
    const dateStr = paymentDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timeStr = paymentDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });

    // Treat every value that has ever meant paid (current + legacy) as paid.
    const isPaid = ['Paid', 'paid', 'collected', 'Completed'].includes(payment.payment.status);

    return (
      <motion.div
        key={payment._id}
        data-testid={`payment-completion-item-${payment._id}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2, delay: index * 0.05 }}
        className="bg-gradient-to-br from-white to-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300"
      >
        <div data-testid={`payment-completion-item-${payment._id}-header`} className="flex items-center justify-between mb-2 sm:mb-3">
          <div data-testid={`payment-completion-item-${payment._id}-status`} className="flex items-center">
            {isPaid ? (
              <CheckCircle data-testid={`payment-completion-item-${payment._id}-status-icon-paid`} className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-1 sm:mr-2" />
            ) : (
              <div data-testid={`payment-completion-item-${payment._id}-status-icon-unpaid`} className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-500 rounded-full mr-1 sm:mr-2"></div>
            )}
            <span data-testid={`payment-completion-item-${payment._id}-status-text`} className="text-xs sm:text-sm font-semibold text-gray-900">
              {payment.payment?.status ? t(`dashboard.${payment.payment.status}`, payment.payment.status) : '-'}
            </span>
          </div>
          <span data-testid={`payment-completion-item-${payment._id}-amount`} className={`text-base sm:text-lg font-bold ${isPaid ? 'text-[#4ABDBD]' : 'text-orange-500'}`}>
            {payment.currency?.currencySymbol || payment.currency?.currencyCode || ''} {payment.price}
          </span>
        </div>

        <div data-testid={`payment-completion-item-${payment._id}-details`} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <div data-testid={`payment-completion-item-${payment._id}-method`} className="flex items-center space-x-2 sm:space-x-3">
            <div data-testid={`payment-completion-item-${payment._id}-method-icon-wrap`} className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isPaid ? 'bg-blue-100' : 'bg-orange-100'
              }`}>
              <CreditCard data-testid={`payment-completion-item-${payment._id}-method-icon`} className={`w-3 h-3 sm:w-4 sm:h-4 ${isPaid ? 'text-blue-600' : 'text-orange-600'}`} />
            </div>
            <div className="min-w-0">
              <p data-testid={`payment-completion-item-${payment._id}-method-label`} className="text-xs font-semibold text-gray-700">
                {t('dashboard.payment_method')}
              </p>
              <p data-testid={`payment-completion-item-${payment._id}-method-value`} className="text-xs sm:text-sm text-gray-600 truncate">
                {payment.payment?.method ? t(`dashboard.${payment.payment.method}`, payment.payment.method) : '-'}
              </p>
            </div>
          </div>

          <div data-testid={`payment-completion-item-${payment._id}-datetime`} className="flex items-center space-x-2 sm:space-x-3">
            <div data-testid={`payment-completion-item-${payment._id}-datetime-icon-wrap`} className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar data-testid={`payment-completion-item-${payment._id}-datetime-icon`} className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p data-testid={`payment-completion-item-${payment._id}-datetime-label`} className="text-xs font-semibold text-gray-700">
                {t('dashboard.date_time')}
              </p>
              <p data-testid={`payment-completion-item-${payment._id}-datetime-value`} className="text-xs sm:text-sm text-gray-600 truncate">
                {dateStr} {t('dashboard.at', 'at')} {timeStr}
              </p>
            </div>
          </div>

          <div data-testid={`payment-completion-item-${payment._id}-speciality`} className="flex items-center space-x-2 sm:space-x-3 sm:col-span-2">
            <div data-testid={`payment-completion-item-${payment._id}-speciality-icon-wrap`} className="w-6 h-6 sm:w-8 sm:h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Tag data-testid={`payment-completion-item-${payment._id}-speciality-icon`} className="w-3 h-3 sm:w-4 sm:h-4 text-pink-600" />
            </div>
            <div className="min-w-0">
              <p data-testid={`payment-completion-item-${payment._id}-speciality-label`} className="text-xs font-semibold text-gray-700">
                {t('dashboard.doctor_speciality')}
              </p>
              <p data-testid={`payment-completion-item-${payment._id}-speciality-value`} className="text-xs sm:text-sm text-gray-600 truncate capitalize">
                <MasterLabel type="masterSpecialty" id={payment.SpecialtyId} field="name" fallback={payment.specialtyName} />
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
        .custom-scrollbar::-webkit-scrollbar-button {
          display: none;
        }
      `}</style>
      <motion.div
        data-testid="payment-completion"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200/50 shadow-md backdrop-blur-sm h-full"
      >
        <div data-testid="payment-completion-header" className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 data-testid="payment-completion-title" className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center">
            <CreditCardIcon data-testid="payment-completion-title-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
            <span data-testid="payment-completion-title-text-xs-up" className="hidden xs:inline">{t('dashboard.recent_payments')}</span>
            <span data-testid="payment-completion-title-text-xs-down" className="xs:hidden">{t('dashboard.recent_payments')}</span>
          </h3>
          <motion.button
            data-testid="payment-completion-view-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/payment")}
            className="text-xs font-medium text-[#4ABDBD] hover:text-[#3A9B9B] transition-colors duration-200 flex items-center cursor-pointer"
          >
            {/* Desktop View */}
            <span data-testid="payment-completion-view-all-desktop" className="hidden sm:inline-flex items-center">
              {t('dashboard.view_all')}
              <ArrowRight data-testid="payment-completion-view-all-desktop-icon" className="w-3 h-3 ml-1" />
            </span>

            {/* Mobile View */}
            <span data-testid="payment-completion-view-all-mobile" className="sm:hidden underline flex items-center">
              {t('dashboard.view_all')}
              <ArrowRight data-testid="payment-completion-view-all-mobile-icon" className="w-3 h-3 ml-1" />
            </span>
          </motion.button>
        </div>

        <div data-testid="payment-completion-body" className="w-full">
          <div data-testid="payment-completion-tabs" className="flex space-x-1 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl p-1 shadow border border-gray-200 mb-4 sm:mb-6">
            <button
              data-testid="payment-completion-tab-paid"
              onClick={() => setActiveTab('paid')}
              className={`relative flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-300 ${activeTab === 'paid'
                ? 'text-white'
                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                }`}
            >
              {activeTab === 'paid' && (
                <motion.div
                  data-testid="payment-completion-tab-active-paid"
                  layoutId="activePaymentTab"
                  className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span data-testid="payment-completion-tab-paid-label" className="relative z-20">{t('dashboard.paid')}</span>
            </button>
            <button
              data-testid="payment-completion-tab-not-paid"
              onClick={() => setActiveTab('notPaid')}
              className={`relative flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-300 ${activeTab === 'notPaid'
                ? 'text-white'
                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                }`}
            >
              {activeTab === 'notPaid' && (
                <motion.div
                  data-testid="payment-completion-tab-active-not-paid"
                  layoutId="activePaymentTab"
                  className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span data-testid="payment-completion-tab-not-paid-label" className="relative z-20">{t('dashboard.not_paid')}</span>
            </button>
          </div>

          <AnimatePresence mode="wait">
            {patientsPaymentLoading ? (
              <div data-testid="payment-completion-loading" className="flex flex-col items-center justify-center py-12">
                <CommonLoader />
                <span data-testid="payment-completion-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_payments')}</span>
              </div>
            ) : (
              <motion.div
                key={activeTab}
                data-testid={`payment-completion-panel-${activeTab === 'paid' ? 'paid' : 'not-paid'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 sm:space-y-4"
              >
                {payments.length > 0 ? (
                  <div data-testid="payment-completion-list" className="max-h-60 sm:max-h-80 overflow-y-auto p-1 sm:p-2 space-y-2 sm:space-y-3 custom-scrollbar">
                    {payments.map((payment, index) => renderPaymentItem(payment, index))}
                  </div>
                ) : (
                  <div data-testid="payment-completion-empty" className="text-center py-8">
                    <CreditCard data-testid="payment-completion-empty-icon" className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p data-testid="payment-completion-empty-text" className="text-sm text-gray-500 font-medium">
                      {activeTab === 'paid' ? t('dashboard.no_paid_payments') : t('dashboard.no_pending_payments')}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </motion.div>
    </>
  );
};

export default PaymentCompletionList;
